import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, TriangleAlert } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import PriorityPill from "@/components/tasks/PriorityPill";
import { useStore } from "@/lib/store";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export default function HitlDrawer() {
  const open = useStore((s) => s.hitlOpen);
  const closeHitl = useStore((s) => s.closeHitl);
  const tasks = useStore((s) => s.tasks);
  const navigate = useNavigate();

  const items = useMemo(
    () =>
      tasks.filter((t) => t.owner === "HUMAN" && t.status !== "done"),
    [tasks],
  );

  const openTask = (id) => {
    closeHitl();
    navigate(`/tasks#task=${id}`);
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) closeHitl();
      }}
    >
      <SheetContent
        data-testid="hitl-drawer"
        className="idea-scroll w-full overflow-y-auto bg-white p-0 sm:max-w-[480px]"
      >
        <SheetTitle className="sr-only">Human action required</SheetTitle>

        <div className="border-b border-black/[0.04] px-7 pb-5 pt-7">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/15 text-orange-700">
              <TriangleAlert className="h-5 w-5" strokeWidth={2.4} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-[18px] font-extrabold tracking-tight text-zinc-900">
                  Human action required
                </h2>
                <span
                  data-testid="hitl-drawer-count"
                  className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-orange-700"
                >
                  {items.length}
                </span>
              </div>
              <p className="mt-0.5 text-[12.5px] text-zinc-500">
                These tasks are blocking your boardroom.
              </p>
            </div>
          </div>
        </div>

        <ul className="divide-y divide-black/[0.04]">
          {items.map((t) => (
            <li
              key={t.id}
              data-testid={`hitl-row-${t.id}`}
              className="flex items-start gap-3 bg-[#FFF7EE]/60 px-7 py-4 transition-colors hover:bg-[#FFF7EE]"
            >
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-bold text-zinc-900">
                  {t.title}
                </div>
                <p className="mt-1 line-clamp-2 text-[12px] text-zinc-600">
                  {t.description}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <PriorityPill priority={t.priority} />
                  <span className="text-[11px] font-semibold text-zinc-500">
                    Due {fmtDate(t.deadline)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                data-testid={`hitl-open-${t.id}`}
                onClick={() => openTask(t.id)}
                className="inline-flex items-center gap-1 rounded-full bg-black px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-zinc-800"
              >
                Open
                <ArrowUpRight className="h-3 w-3" strokeWidth={2.6} />
              </button>
            </li>
          ))}
          {items.length === 0 && (
            <li className="px-7 py-10 text-center text-[12.5px] text-zinc-400">
              All clear — no human-blocking tasks right now.
            </li>
          )}
        </ul>

        <div className="sticky bottom-0 border-t border-black/[0.04] bg-white px-7 py-4">
          <button
            type="button"
            data-testid="hitl-view-all"
            onClick={() => {
              closeHitl();
              navigate("/tasks");
            }}
            className="inline-flex w-full items-center justify-center gap-1 rounded-full border border-black/[0.06] bg-white px-4 py-2 text-[12.5px] font-semibold text-zinc-800 transition-colors hover:bg-zinc-50"
          >
            View all tasks
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.4} />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
